import { useEffect, useState } from 'react';
import { ShoppingCart, ArrowRight, Tag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '@/hooks/useCart';

export function FloatingCartButton() {
  const { cart } = useCart();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  // Calculate total price using the same logic as ShoppingCart
  const calculateTotal = () => {
    const selectedVendor = JSON.parse(localStorage.getItem('selectedVendor'));
    const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const mrpTotal = cart.reduce((sum, item) => sum + item.mrp * item.quantity, 0);
    const discount = mrpTotal - subtotal;
    const platformFees = 0;
    const deliveryCharges = selectedVendor?.deliveryChargeWaived
      ? selectedVendor?.deliveryCharge || 0
      : 0;
    const totalAmount = subtotal + platformFees + deliveryCharges;
    const minimumOrderValue = selectedVendor?.minimumOrder || 0;
    const minimumOrderValueMet = subtotal < minimumOrderValue;

    return {
      subtotal,
      mrpTotal,
      discount,
      platformFees,
      deliveryCharges,
      total: totalAmount,
      minimumOrderValue,
      minimumOrderValueMet,
    };
  };

  const totals = calculateTotal();
  const hasDiscount = totals.discount > 0;

  useEffect(() => {
    if (cart.length > 0) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [cart.length]);

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed max-w-md w-[22rem] mx-auto bottom-16 left-4 right-4 z-50"
        >
          <div
            onClick={() => navigate('/cart')}
            className="cursor-pointer bg-gradient-to-r from-emerald-500 to-green-500 rounded-full shadow-xl  flex items-center justify-between p-1 pr-4 text-white hover:shadow-xl hover:scale-[1.0001] transition-all duration-300"
          >
            <div className="flex items-center">
              <div className="bg-white rounded-full p-2 mr-3 relative">
                <ShoppingCart className="h-5 w-5 text-green-600" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium opacity-90">Your Cart</span>
                <span className="font-bold">
                  {cart.length} {cart.length === 1 ? 'item' : 'items'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {hasDiscount && (
                <div className="bg-white/20 rounded-full py-0.5 px-2 text-xs flex items-center">
                  <Tag className="h-3 w-3 mr-1" />
                  <span>Save ₹{Math.floor(totals.discount)}</span>
                </div>
              )}
              <span className="font-bold text-lg mx-1">₹{Math.ceil(totals.subtotal)}</span>
              <div className="bg-white/20 rounded-full p-1">
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
